import React, { useEffect, useState, useCallback } from 'react';
import { Layout, Menu, Typography, Card, Table, Modal, Form, Input, Select, DatePicker, Button, Tag, Upload, Image, message, Space, Descriptions, Avatar, Drawer, List, Empty, Spin, Radio, Alert, Tooltip } from 'antd';
import { UserOutlined, TeamOutlined, PlusOutlined, EditOutlined, DeleteOutlined, LogoutOutlined, MenuUnfoldOutlined, MenuFoldOutlined, UploadOutlined, TrophyOutlined, UserSwitchOutlined, ReloadOutlined, DashboardOutlined, CalendarOutlined } from '@ant-design/icons';
import { useAuth } from '../services/useAuth';
import { teacherAPI, studentAPI, scheduleAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import '../styles/common.css';
import { RcFile } from 'antd/lib/upload';
import { UploadFile } from 'antd/lib/upload/interface';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const { Header, Sider, Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// 初始化日历本地化
const localizer = momentLocalizer(moment);

// Define Breakpoint type locally
type Breakpoint = 'xxl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs';

// Remove FixedType import and define it locally
type FixedType = 'left' | 'right' | boolean;

// 定义扩展的user类型，包含可选的avatar字段
type UserWithAvatar = {
  userId: number | null;
  username: string | null;
  userType: string | null;
  avatar?: string;
};

// Define student achievement interface type
interface StudentAchievement {
  id: number;
  studentId: number;
  achievementDate: string;
  description: string;
  title?: string;
  imageUrl?: string;
  errorMessage?: string; // 添加错误信息字段，用于处理图片上传失败情况
  // Add any other possible attributes
}

// 定义课程事件类型
interface CourseEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  studentId: number;
  studentName: string;
  courseType?: string;
  checkedIn?: boolean;  // 添加签到状态
  checkInTime?: Date;   // 添加签到时间
}

// 扩展RcFile类型以支持preview属性
interface ExtendedRcFile extends RcFile {
  preview?: string;
}

const ParentDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [achievements, setAchievements] = useState<StudentAchievement[]>([]);
  
  // 添加课程管理相关状态
  const [activeMenuKey, setActiveMenuKey] = useState<string>('students');
  const [courseEvents, setCourseEvents] = useState<CourseEvent[]>([]);
  const [courseDetailModalVisible, setCourseDetailModalVisible] = useState(false);
  const [selectedCourseEvent, setSelectedCourseEvent] = useState<CourseEvent | null>(null);
  
  // 添加一个状态标记服务器错误，避免反复请求导致错误
  const [teacherServerError, setTeacherServerError] = useState<boolean>(false);
  
  // Modal状态
  const [isAddStudentModalVisible, setIsAddStudentModalVisible] = useState(false);
  const [isEditStudentModalVisible, setIsEditStudentModalVisible] = useState(false);
  const [isAchievementModalVisible, setIsAchievementModalVisible] = useState(false);
  const [isSelectTeacherModalVisible, setIsSelectTeacherModalVisible] = useState(false);
  const [isAchievementDetailsModalVisible, setIsAchievementDetailsModalVisible] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState<StudentAchievement | null>(null);
  
  // Loading状态
  const [loading, setLoading] = useState({
    teachers: false,
    students: false,
    achievements: false,
    courses: false
  });

  // 响应式布局相关状态
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // 表单实例
  const [studentForm] = Form.useForm();
  const [teacherForm] = Form.useForm();
  const [achievementForm] = Form.useForm();

  // 添加一个状态来标记已尝试加载教师
  const [teachersLoadAttempted, setTeachersLoadAttempted] = useState(false);

  // 处理头像加载错误 - 使用与主页相同的默认头像
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>): boolean => {
    const target = e.currentTarget as HTMLImageElement;
    target.src = `${process.env.PUBLIC_URL}/images/default-teacher.jpg`;
    // 返回false以防止使用icon作为后备
    return false;
  };

  // 响应窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
    };

    // 初始检查
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 切换侧边栏显示/隐藏 - 移动端不再需要
  const toggleSidebar = () => {
    if (isMobile) {
      setDrawerVisible(!drawerVisible);
    } else {
      setCollapsed(!collapsed);
    }
  };

  // 处理菜单点击
  const handleMenuClick = (key: string) => {
    setActiveMenuKey(key);
    if (isMobile) {
      setDrawerVisible(false);
    }
    
    // 如果切换到课程安排菜单，加载课程
    if (key === 'courses' && user?.userId) {
      loadParentCourses();
    }
    
    // 如果从学习成果页面切换到其他页面，重置选中的学生
    if (selectedStudent && key !== 'achievements') {
      setSelectedStudent(null);
    }
  };
  
  // 加载家长的所有学员课程安排
  const loadParentCourses = useCallback(async () => {
    if (!user?.userId) {
      return;
    }
    
    setLoading(prev => ({ ...prev, courses: true }));
    
    try {
      const coursesData = await scheduleAPI.getParentSchedules(user.userId);
      
      if (Array.isArray(coursesData)) {
        // 将API返回的数据转换为Calendar组件所需的格式
        const events: CourseEvent[] = coursesData.map(course => {
          // 查找对应学生的授课类型
          const student = students.find(s => s.id === course.studentId);
          const studentCourseType = student ? student.courseType : undefined;
          
          return {
            id: course.id,
            title: `课程 - ${course.studentName}`,
            start: new Date(course.startTime),
            end: new Date(course.endTime),
            studentId: course.studentId,
            studentName: course.studentName,
            // 优先使用学生的授课类型，如果没有则使用课程内容
            courseType: studentCourseType || course.courseContent,
            checkedIn: course.checkedIn,
            checkInTime: course.checkInTime ? new Date(course.checkInTime) : undefined
          };
        });
        
        setCourseEvents(events);
        message.success(`成功加载${events.length}个课程安排`);
      }
    } catch (error) {
      console.error('加载课程安排失败:', error);
      message.error('加载课程安排失败，请稍后重试');
    } finally {
      setLoading(prev => ({ ...prev, courses: false }));
    }
  }, [user?.userId, students]);

  // 错误处理函数
  const handleApiError = useCallback((error: any, errorMessage: string) => {
    console.error(`${errorMessage}:`, error);
    if (error.response?.status === 401) {
      // 身份验证失败，可能是token过期
      message.error('登录已过期，请重新登录');
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
    } else {
      message.error(errorMessage);
    }
  }, [logout, navigate]);

  // 添加重试请求的通用函数
  const retryApiCall = async <T,>(apiCall: () => Promise<T>, retries = 2, delay = 1000): Promise<T> => {
    try {
      return await apiCall();
    } catch (error: any) {
      if (retries > 0 && error.response?.status === 500) {
        console.log(`服务器错误 (500)，${delay/1000}秒后重试... 剩余重试次数: ${retries}`);
        // 简单的延迟函数
        await new Promise(resolve => setTimeout(resolve, delay));
        return retryApiCall(apiCall, retries - 1, delay * 1.5);
      }
      throw error;
    }
  };

  // 加载教师列表，添加重试机制
  const loadTeachers = useCallback(async () => {
    // 如果之前已经确认过服务器有错误，不再尝试请求
    if (teacherServerError) {
      console.log('服务器接口已确认存在错误，跳过教师列表请求');
      return [];
    }
    
    setTeachersLoadAttempted(true); // 标记已尝试加载
    setLoading(prev => ({ ...prev, teachers: true }));
    try {
      // 使用重试机制
      const data = await retryApiCall(() => teacherAPI.getAllTeachers());
      if (data && Array.isArray(data)) {
        setTeachers(data);
        setTeacherServerError(false); // 请求成功，重置错误状态
        
        // 添加调试日志，检查教师头像字段
        if (data.length > 0) {
          console.log('教师数据示例:', data[0]);
          console.log('头像相关字段:', {
            avatar: data[0].avatar,
            profileImage: data[0].profileImage
          });
        }
      } else {
        console.warn('API返回的教师数据格式不正确:', data);
        setTeachers([]);
      }
      return data;
    } catch (error: any) {
      const errorMessage = error.response?.status === 500 
        ? '服务器内部错误 (500)，请联系管理员' 
        : `加载教师列表失败: ${error.message || '未知错误'}`;
      
      console.error('加载教师列表失败:', error);
      console.error('错误详情:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        method: error.config?.method,
        data: error.response?.data
      });
      
      // 如果是500错误，标记服务器接口有问题，避免反复请求
      if (error.response?.status === 500) {
        setTeacherServerError(true);
      }
      
      message.error(errorMessage);
      setTeachers([]);
      throw error;
    } finally {
      setLoading(prev => ({ ...prev, teachers: false }));
    }
  }, [teacherServerError]);

  // 加载学生列表
  const loadStudents = useCallback(async () => {
    if (!user?.userId) return;
    
    setLoading(prev => ({ ...prev, students: true }));
    try {
      const data = await studentAPI.getStudentsByParent(user.userId);
      if (data && Array.isArray(data)) {
        setStudents(data);
      } else {
        console.warn('API返回的学生数据格式不正确:', data);
        setStudents([]);
      }
    } catch (error) {
      console.error('加载学生列表失败:', error);
      setStudents([]);
    } finally {
      setLoading(prev => ({ ...prev, students: false }));
    }
  }, [user?.userId]);

  // 加载学生成就
  const fetchStudentAchievements = useCallback(async (studentId: number) => {
    if (!studentId) {
      message.error('无效的学生ID');
      return;
    }
    
    setLoading(prev => ({ ...prev, achievements: true }));
    setAchievements([]); // 清空现有数据，避免加载失败时显示旧数据
    
    try {
      const hide = message.loading('正在加载学习成果...');
      
      try {
        const data = await studentAPI.getAchievements(studentId);
        
        if (data && Array.isArray(data)) {
          // 检查每个成就项的图片URL
          data.forEach((item, index) => {
            console.log(`学习成果[${index}] 图片URL:`, item.imageUrl);
            if (!item.imageUrl && item.imageUrl !== null && item.imageUrl !== undefined) {
              console.warn(`学习成果[${index}] 图片URL为空字符串`);
            }
          });
          
          setAchievements(data);
          
          if (data.length > 0) {
            console.log('成功加载成就数据，示例:', data[0]);
            console.log('成就数据中的图片URLs:', data.map(item => item.imageUrl).filter(Boolean));
            message.success(`成功加载${data.length}条学习成果记录`);
          } else {
            console.log('没有找到学习成果数据');
            message.info('该学生暂无学习成果记录');
          }
        } else {
          console.warn('学习成果数据格式不正确:', data);
          message.warning('获取到的学习成果数据格式不正确');
          setAchievements([]);
        }
      } finally {
        hide(); // 确保加载提示被关闭
      }
    } catch (error: any) {
      // 详细的错误日志
      console.error('加载学习成果失败:', error);
      console.error('错误详情:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        method: error.config?.method,
        data: error.response?.data
      });
      
      // 根据错误类型提供不同的用户提示
      let errorMsg = '加载学习成果失败';
      
      if (error.response?.status === 500) {
        errorMsg = '服务器内部错误，请稍后再试';
      } else if (error.response?.status === 404) {
        errorMsg = '未找到学生学习成果数据';
      } else if (error.message.includes('timeout')) {
        errorMsg = '加载超时，请检查网络连接';
      } else if (error.message.includes('Network Error')) {
        errorMsg = '网络连接错误，请检查您的网络';
      }
      
      message.error(errorMsg);
    } finally {
      setLoading(prev => ({ ...prev, achievements: false }));
    }
  }, []);

  // 初始化 - 加载教师列表和学生列表
  useEffect(() => {
    if (user?.userId && !teachersLoadAttempted) {
      loadTeachers();
      // 延迟加载学生列表，确保不会立即显示错误
      setTimeout(() => {
        loadStudents();
      }, 500);
    }
  }, [user, teachersLoadAttempted, loadTeachers, loadStudents]);

  // 处理登出
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // 显示添加学生模态框
  const showAddStudentModal = () => {
    studentForm.resetFields();
    setIsAddStudentModalVisible(true);
  };

  // 显示编辑学生模态框
  const showEditStudentModal = (student: any) => {
    studentForm.setFieldsValue({
      id: student.id,
      name: student.name,
      birthDate: student.birthDate ? moment(student.birthDate) : null,
      gender: student.gender,
      grade: student.grade,
      courseType: student.courseType,
      courseMode: student.courseMode
    });
    setIsEditStudentModalVisible(true);
  };

  // 显示选择教师模态框
  const showSelectTeacherModal = (student: any) => {
    // 阻止设置selectedStudent为学生，这会导致UI切换到学习成果页面
    // 我们只在模态框内使用这个学生信息
    const studentForModal = { ...student };
    setSelectedStudent(null); // 确保不会切换到学习成果页面
    
    // 预先选中当前学生的教师（如果有）
    teacherForm.setFieldsValue({
      teacherId: student.teacherId
    });
    
    // 将学生ID保存在一个临时变量中，供选择教师时使用
    teacherForm.setFieldsValue({
      studentId: student.id
    });
    
    setIsSelectTeacherModalVisible(true);
    
    // 如果之前已确认服务器有错误，就不触发加载
    if (!teacherServerError) {
      // 每次打开模态框时都重新加载教师列表，确保数据是最新的
      loadTeachers().catch(error => {
        const errorMessage = error.response?.status === 500 
          ? '服务器内部错误 (500)，无法加载教师列表，请稍后重试或联系管理员' 
          : `加载教师列表失败: ${error.message || '未知错误'}`;
        
        message.error(errorMessage);
        console.error('加载教师列表失败:', error);
      });
    } else {
      // 如果已知服务器有错误，显示友好提示
      message.warning('教师列表加载功能暂时不可用，请联系管理员', 3);
    }
  };
  
  // 处理选择教师，添加重试机制
  const handleSelectTeacher = async (e?: React.MouseEvent<HTMLElement>) => {
    if (e && 'preventDefault' in e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const values = await teacherForm.validateFields();
      
      // 使用表单中的studentId而不是selectedStudent
      const studentId = values.studentId;
      
      if (!studentId) {
        message.error('未找到学生信息');
        return;
      }
      
      // 使用重试机制
      await retryApiCall(() => studentAPI.updateStudentTeacher(studentId, values.teacherId));
      message.success('更新教师成功');
      setIsSelectTeacherModalVisible(false);
      loadStudents(); // 重新加载学生列表
    } catch (error: any) {
      // 提供更详细的错误信息
      const errorMessage = error.response?.status === 500 
        ? '服务器内部错误 (500)，请联系管理员' 
        : `更新教师失败: ${error.message || '未知错误'}`;
      
      console.error('更新教师失败:', error);
      console.error('错误详情:', {
        studentId: teacherForm.getFieldValue('studentId'),
        teacherId: teacherForm.getFieldValue('teacherId'),
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        method: error.config?.method,
        data: error.response?.data
      });
      
      message.error(errorMessage);
    }
  };
  
  // 处理添加学生
  const handleAddStudent = async () => {
    try {
      const values = await studentForm.validateFields();
      if (!user?.userId) return;
      
      // 添加详细的调试日志
      console.log('添加学生表单原始值:', values);
      console.log('授课类型(courseType):', values.courseType);
      console.log('授课方式(courseMode):', values.courseMode);
      
      // 转换出生日期格式
      const formattedValues = {
        ...values,
        birthDate: values.birthDate ? values.birthDate.format('YYYY-MM-DD') : null
      };
      
      console.log('发送到后端的数据:', formattedValues);
      
      const result = await studentAPI.addStudent(user.userId, formattedValues);
      console.log('后端添加响应:', result);
      
      message.success('添加学生成功');
      setIsAddStudentModalVisible(false);
      
      // 重新加载学生列表
      await loadStudents();
      
      // 如果添加成功且返回了学生信息，直接打开选择教师模态框
      if (result && result.id) {
        setTimeout(() => {
          message.info('请为新添加的学员选择教师');
          showSelectTeacherModal(result);
        }, 500);
      } else {
        setTimeout(() => {
          message.info('请为新添加的学员选择教师');
        }, 500);
      }
    } catch (error) {
      message.error('添加学生失败');
      console.error('添加学生失败:', error);
    }
  };

  // 处理编辑学生
  const handleEditStudent = async () => {
    try {
      const values = await studentForm.validateFields();
      
      // 添加详细的调试日志
      console.log('编辑学生表单原始值:', values);
      console.log('授课类型(courseType):', values.courseType);
      console.log('授课方式(courseMode):', values.courseMode);
      
      // 转换出生日期格式
      const formattedValues = {
        ...values,
        birthDate: values.birthDate ? values.birthDate.format('YYYY-MM-DD') : null
      };
      
      console.log('发送到后端的数据:', formattedValues);
      
      const response = await studentAPI.updateStudent(values.id, formattedValues);
      console.log('后端更新响应:', response);
      
      message.success('更新学生信息成功');
      setIsEditStudentModalVisible(false);
      loadStudents(); // 重新加载学生列表
    } catch (error) {
      message.error('更新学生信息失败');
      console.error('更新学生信息失败:', error);
    }
  };

  // 处理删除学生
  const handleDeleteStudent = async (studentId: number) => {
    try {
      await studentAPI.deleteStudent(studentId);
      message.success('删除学生成功');
      loadStudents(); // 重新加载学生列表
    } catch (error) {
      message.error('删除学生失败');
      console.error('删除学生失败:', error);
    }
  };

  // 显示学生成就
  const showStudentAchievements = (student: any, fromButton = false) => {
    // 只有通过按钮点击才允许显示成就
    if (!fromButton) {
      return;
    }
    setSelectedStudent(student);
    fetchStudentAchievements(student.id);
  };
  
  // 显示添加成就模态框
  const showAddAchievementModal = () => {
    achievementForm.resetFields();
    setSelectedAchievement(null);
    setIsAchievementModalVisible(true);
  };
  
  // 显示成就详情模态框
  const showAchievementDetails = (achievement: StudentAchievement) => {
    // 不再使用详情模态框，保留函数但不执行任何操作
    console.log('详情功能已移除');
  };
  
  // 处理添加成就
  const handleAddAchievement = async () => {
    try {
      const values = await achievementForm.validateFields();
      
      // 检查是否有图片需要上传 - 修改检查逻辑
      const hasFileList = values.image && Array.isArray(values.image) && values.image.length > 0;
      const hasNestedFileList = values.image && values.image.fileList && Array.isArray(values.image.fileList) && values.image.fileList.length > 0;
      
      // 确定图片来源
      let imageFile: File | undefined;
      if (hasFileList && values.image[0].originFileObj) {
        // 直接从数组中获取
        imageFile = values.image[0].originFileObj as File;
        console.log('从数组中获取图片对象');
      } else if (hasNestedFileList && values.image.fileList[0].originFileObj) {
        // 从嵌套结构中获取
        imageFile = values.image.fileList[0].originFileObj as File;
        console.log('从嵌套结构中获取图片对象');
      }
      
      // 记录图片信息
      if (imageFile) {
        console.log('已获取图片对象:', {
          name: imageFile.name,
          type: imageFile.type,
          size: Math.round(imageFile.size / 1024) + 'KB',
          lastModified: new Date(imageFile.lastModified).toISOString()
        });
      } else {
        console.log('未能获取有效的图片对象');
      }
      
      // 将日期转换为字符串
      const formattedValues = {
        ...values,
        achievementDate: values.achievementDate.format('YYYY-MM-DD'),
        // 设置图片对象
        image: imageFile
      };
      
      // 详细日志输出准备上传的数据
      console.log('准备上传的学习成果数据:', {
        title: formattedValues.title,
        achievementDate: formattedValues.achievementDate,
        description: formattedValues.description ? formattedValues.description.substring(0, 20) + '...' : '无',
        hasImage: !!formattedValues.image,
        imageSize: formattedValues.image ? Math.round(formattedValues.image.size / 1024) + 'KB' : '无'
      });
      
      if (selectedStudent) {
        // 创建更详细的加载状态提示
        let hide: any;
        if (formattedValues.image) {
          hide = message.loading({
            content: '正在上传学习成果和证明图片...',
            duration: 0
          });
        } else {
          hide = message.loading({
            content: '正在保存学习成果...',
            duration: 0
          });
        }
        
        try {
          const result = await studentAPI.addAchievement(selectedStudent.id, formattedValues) as StudentAchievement;
          
          // 清除加载状态
          hide();
          
          // 详细记录返回结果
          console.log('成果添加结果:', {
            id: result.id,
            title: result.title,
            achievementDate: result.achievementDate,
            hasImage: !!result.imageUrl,
            imageUrl: result.imageUrl || '无',
            errorMessage: result.errorMessage || '无'
          });
          
          // 处理服务器返回的错误信息
          if (result.errorMessage) {
            console.warn('添加成果时发生错误:', result.errorMessage);
            message.warning({
              content: result.errorMessage,
              duration: 5
            });
          } 
          // 验证图片上传结果
          else if (!result.imageUrl) {
            console.error('证明图片上传失败: 服务端返回的imageUrl为空');
            message.warning('证明图片上传失败，但其他信息已保存成功');
          }
          
          // 构建更友好的成功提示信息
          const successContent = (
            <div>
              <div><strong>学习成果添加成功</strong></div>
              <div>名称: {result.title || '未命名'}</div>
              <div>日期: {moment(result.achievementDate).format('YYYY-MM-DD')}</div>
              <div>图片: {result.imageUrl ? '已上传' : '无'}</div>
              {result.errorMessage && <div style={{ color: '#ff4d4f' }}>注意: {result.errorMessage}</div>}
            </div>
          );
          
          // 显示成功提示
          message.success({
            content: successContent,
            duration: 3
          });
          
          // 刷新学习成果列表
          if (selectedStudent) {
            fetchStudentAchievements(selectedStudent.id);
          }
          
          // 关闭模态框
          setIsAchievementModalVisible(false);
          
          // 重置表单
          achievementForm.resetFields();
          
        } catch (error: any) {
          // 清除加载状态
          hide();
          
          // 显示错误消息
          const errorMsg = error.message || '添加学习成果失败';
          message.error(errorMsg);
          console.error('添加学习成果发生错误:', error);
          
          // 检查是否与图片上传相关
          if (errorMsg.includes('图片') || errorMsg.includes('文件')) {
            message.warning('图片上传失败，请尝试使用更小的图片 (小于2MB) 或不同的图片格式');
          }
        }
      }
    } catch (error) {
      console.error('表单验证失败:', error);
      message.error('请填写必填项');
    }
  };
  
  // 处理删除成就
  const handleDeleteAchievement = async (achievementId: number) => {
    try {
      await studentAPI.deleteAchievement(achievementId);
      
      // 更新本地数据
      setAchievements(prev => prev.filter(item => item.id !== achievementId));
      
      message.success('学习成果已成功删除');
    } catch (error) {
      console.error('删除学习成果失败:', error);
      message.error('删除学习成果失败，请稍后重试');
    }
  };

  // 判断是否可以签到（当天和过去的课程可以，未来日期的课程不能）
  const isCheckInEligible = (courseDate: Date | undefined): boolean => {
    if (!courseDate) return false;
    
    const now = new Date();
    const courseDay = new Date(
      courseDate.getFullYear(), 
      courseDate.getMonth(), 
      courseDate.getDate()
    );
    const today = new Date(
      now.getFullYear(), 
      now.getMonth(), 
      now.getDate()
    );
    
    // 当天或过去的课程可以签到
    return courseDay.getTime() <= today.getTime();
  };

  // 判断是否是当天课程
  const isTodayCourse = (courseDate: Date | undefined): boolean => {
    if (!courseDate) return false;
    const today = new Date();
    return courseDate.getDate() === today.getDate() &&
           courseDate.getMonth() === today.getMonth() &&
           courseDate.getFullYear() === today.getFullYear();
  };
  
  // 判断是否是过去的课程
  const isPastCourse = (courseDate: Date | undefined): boolean => {
    if (!courseDate) return false;
    const now = new Date();
    // 设置now为今天的开始（00:00:00）
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    return courseDate.getTime() < today.getTime();
  };
  
  // 处理课程点击事件
  const handleSelectEvent = (event: CourseEvent) => {
    setSelectedCourseEvent(event);
    setCourseDetailModalVisible(true);
  };
  
  // 处理课程签到
  const handleCheckIn = async (scheduleId: number) => {
    try {
      await scheduleAPI.checkInSchedule(scheduleId);
      message.success('签到成功！');
      
      // 更新本地数据
      setCourseEvents(prev => 
        prev.map(event => 
          event.id === scheduleId 
            ? { ...event, checkedIn: true, checkInTime: new Date() } 
            : event
        )
      );
      
      // 更新选中的课程事件
      if (selectedCourseEvent && selectedCourseEvent.id === scheduleId) {
        setSelectedCourseEvent({
          ...selectedCourseEvent,
          checkedIn: true,
          checkInTime: new Date()
        });
      }
    } catch (error) {
      console.error('签到失败:', error);
      message.error('签到失败，请稍后重试');
    }
  };

  // 自定义事件渲染方法
  const eventPropGetter = useCallback(
    (event: CourseEvent) => {
      return {
        style: {
          backgroundColor: '#1890ff',
        }
      };
    },
    []
  );

  // 自定义月视图事件渲染
  const monthEvent = useCallback(
    ({ event }: { event: CourseEvent }) => (
      <div>
        <span>{event.studentName}</span>
      </div>
    ),
    []
  );

  // 自定义周视图事件渲染
  const weekEvent = useCallback(
    ({ event }: { event: CourseEvent }) => (
      <div>
        <div><strong>{event.studentName}</strong></div>
        <div>{event.courseType}</div>
      </div>
    ),
    []
  );
  
  // 自定义工具栏组件
  const CustomToolbar = useCallback(
    (props: any) => {
      const goToBack = () => {
        props.onNavigate('PREV');
      };
      
      const goToNext = () => {
        props.onNavigate('NEXT');
      };
      
      const goToCurrent = () => {
        props.onNavigate('TODAY');
      };
      
      const label = () => {
        return (
          <span>{props.label}</span>
        );
      };
      
      // 根据当前视图确定显示的标签文本
      const prevLabel = props.view === 'week' ? '上一周' : '上一月';
      const nextLabel = props.view === 'week' ? '下一周' : '下一月';
      
      return (
        <div className="rbc-toolbar">
          <span className="rbc-btn-group">
            <button type="button" onClick={goToCurrent}>今天</button>
            <button type="button" onClick={goToBack}>{prevLabel}</button>
            <button type="button" onClick={goToNext}>{nextLabel}</button>
          </span>
          <span className="rbc-toolbar-label">{label()}</span>
          <span className="rbc-btn-group">
            <button 
              type="button" 
              className={props.view === 'month' ? 'rbc-active' : ''} 
              onClick={() => props.onView('month')}
            >
              月视图
            </button>
            <button 
              type="button" 
              className={props.view === 'week' ? 'rbc-active' : ''} 
              onClick={() => props.onView('week')}
            >
              周视图
            </button>
          </span>
        </div>
      );
    },
    []
  );
  
  // 渲染课程安排日历组件
  const renderCourseSchedule = () => {
    // 创建每天8:00 AM和9:00 PM的时间对象
    const minTime = new Date();
    minTime.setHours(8, 0, 0);
    
    const maxTime = new Date();
    maxTime.setHours(21, 0, 0);
    
    return (
      <div className="course-schedule-container">
        <div className="section-header">
          <Title level={4}>课程管理</Title>
        </div>
        
        <div className="calendar-container" style={{ height: 'calc(100vh - 240px)' }}>
          <Calendar
            localizer={localizer}
            events={courseEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            views={['month', 'week']}
            defaultView='week'
            min={minTime}
            max={maxTime}
            formats={{
              dayHeaderFormat: (date) => moment(date).format('YYYY年MM月DD日'),
              dayRangeHeaderFormat: ({ start, end }) => 
                `${moment(start).format('YYYY年MM月DD日')} - ${moment(end).format('MM月DD日')}`,
            }}
            messages={{
              today: '今天',
              previous: '上一个',
              next: '下一个',
              month: '月视图',
              week: '周视图',
              date: '日期',
              time: '时间',
              event: '事件',
              showMore: (total) => `+${total}更多`
            }}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventPropGetter}
            components={{
              month: {
                event: monthEvent
              },
              week: {
                event: weekEvent
              },
              toolbar: CustomToolbar
            }}
          />
        </div>
        
        {/* 课程详情模态框 */}
        <Modal
          title="课程详情"
          open={courseDetailModalVisible}
          onCancel={() => setCourseDetailModalVisible(false)}
          footer={[
            selectedCourseEvent && !selectedCourseEvent.checkedIn && isCheckInEligible(selectedCourseEvent.start) && (
              <Button 
                key="checkin" 
                type="primary" 
                onClick={() => selectedCourseEvent && handleCheckIn(selectedCourseEvent.id)}
              >
                {isTodayCourse(selectedCourseEvent.start) ? '签到' : '补签'}
              </Button>
            ),
            <Button key="close" onClick={() => setCourseDetailModalVisible(false)}>
              关闭
            </Button>
          ]}
        >
          {selectedCourseEvent && (
            <>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="学员姓名">{selectedCourseEvent.studentName}</Descriptions.Item>
                <Descriptions.Item label="课程类型">{selectedCourseEvent.courseType || '未指定'}</Descriptions.Item>
                <Descriptions.Item label="开始时间">
                  {moment(selectedCourseEvent.start).format('YYYY-MM-DD HH:mm')}
                </Descriptions.Item>
                <Descriptions.Item label="结束时间">
                  {moment(selectedCourseEvent.end).format('YYYY-MM-DD HH:mm')}
                </Descriptions.Item>
                <Descriptions.Item label="签到状态">
                  {selectedCourseEvent.checkedIn ? (
                    <Tag color="green">已签到</Tag>
                  ) : (
                    <Tag color="orange">未签到</Tag>
                  )}
                </Descriptions.Item>
              </Descriptions>
              
              {isTodayCourse(selectedCourseEvent.start) && (
                <Alert
                  style={{ marginTop: 16 }}
                  message="今日课程可以签到"
                  description="今日课程可以签到，教师端将会收到签到提醒。"
                  type="info"
                  showIcon
                />
              )}
              
              {isPastCourse(selectedCourseEvent.start) && (
                <Alert
                  style={{ marginTop: 16 }}
                  message="可以为历史课程补签"
                  description="为历史课程补签，教师端将会收到补签提醒。"
                  type="info"
                  showIcon
                />
              )}
              
              {!isCheckInEligible(selectedCourseEvent.start) && (
                <Alert
                  style={{ marginTop: 16 }}
                  message="未来课程不能签到"
                  description="课程还未开始，无法进行签到操作。"
                  type="warning"
                  showIcon
                />
              )}
            </>
          )}
        </Modal>
      </div>
    );
  };
  
  // 在组件挂载和用户ID变化时加载课程
  useEffect(() => {
    if (user?.userId && activeMenuKey === 'courses') {
      // 确保先加载学生数据，再加载课程
      if (students.length === 0) {
        loadStudents().then(() => {
          loadParentCourses();
        });
      } else {
        loadParentCourses();
      }
    }
  }, [user?.userId, activeMenuKey, loadParentCourses, students.length, loadStudents]);

  // 渲染侧边栏菜单
  const sideMenu = (
    <Menu
      theme="dark"
      selectedKeys={[activeMenuKey]}
      mode="inline"
      onClick={({key}) => handleMenuClick(key)}
    >
      <Menu.Item key="students" icon={<TeamOutlined />}>
        学员管理
      </Menu.Item>
      <Menu.Item key="courses" icon={<CalendarOutlined />}>
        课程管理
      </Menu.Item>
      <Menu.Item key="live" icon={<DashboardOutlined />}>
        课程直播
      </Menu.Item>
    </Menu>
  );

  // 移动端顶部菜单
  const mobileTopMenu = (
    <Menu
      mode="horizontal"
      selectedKeys={[activeMenuKey]}
      onClick={({key}) => handleMenuClick(key)}
      style={{ lineHeight: '46px' }}
    >
      <Menu.Item key="students" icon={<TeamOutlined />}>
        学员管理
      </Menu.Item>
      <Menu.Item key="courses" icon={<CalendarOutlined />}>
        课程管理
      </Menu.Item>
      <Menu.Item key="live" icon={<DashboardOutlined />}>
        课程直播
      </Menu.Item>
    </Menu>
  );
  
  return (
    <>
      {/* 添加全局禁用表格行点击的样式 */}
      <style>
        {`
          .ant-table-row-no-hover:hover > td {
            background: inherit !important;
            cursor: default !important;
          }
          .ant-table-tbody > tr > td {
            cursor: default !important;
          }
          
          /* 阻止表单默认提交行为导致的页面跳转 */
          form {
            pointer-events: auto !important;
          }
          
          /* 确保模态框不会导致页面跳转 */
          .ant-modal-content, 
          .ant-modal-header,
          .ant-modal-body,
          .ant-modal-footer {
            pointer-events: auto !important;
          }
          
          /* 确保点击按钮不会导致页面跳转 */
          button {
            pointer-events: auto !important;
          }

          /* 移动端顶部样式 */
          .mobile-top-container {
            background: #fff;
            padding: 8px 16px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            margin-bottom: 12px;
          }
          
          .mobile-top-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 0;
          }
          
          .mobile-top-title {
            display: flex;
            align-items: center;
          }
          
          .mobile-user-info {
            display: flex;
            align-items: center;
            gap: 8px;
          }
        `}
      </style>
      <Layout style={{ minHeight: '100vh' }}>
        {!isMobile && (
          <Sider 
            collapsible 
            collapsed={collapsed} 
            onCollapse={value => setCollapsed(value)}
            breakpoint="lg"
            theme="dark"
          >
            <div className="logo" style={{ height: '64px', background: '#001529', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DashboardOutlined style={{ fontSize: '20px', marginRight: collapsed ? '0' : '8px' }} />
              {!collapsed && <span>家长控制面板</span>}
            </div>
            {sideMenu}
          </Sider>
        )}
        <Layout>
          {!isMobile ? (
            <Header style={{ padding: 0, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <div style={{ marginRight: '16px' }}>
                <Space>
                  <span>欢迎，{user?.username || '家长'}</span>
                  <Button type="text" icon={<LogoutOutlined />} onClick={handleLogout}>退出</Button>
                </Space>
              </div>
            </Header>
          ) : (
            <div className="mobile-top-container">
              <div className="mobile-top-header">
                <div className="mobile-top-title">
                  <DashboardOutlined style={{ fontSize: '20px', marginRight: '8px', color: '#1890ff' }} />
                  <Title level={5} style={{ margin: 0 }}>家长控制面板</Title>
                </div>
                <div className="mobile-user-info">
                  <span style={{ fontSize: '14px' }}>欢迎，{user?.username || '家长'}</span>
                  <Button size="small" type="text" icon={<LogoutOutlined />} onClick={handleLogout}>退出</Button>
                </div>
              </div>
              {mobileTopMenu}
            </div>
          )}
          <Content className={isMobile ? "content-container-mobile" : "content-container"}>
            {activeMenuKey === 'live' ? (
              <div className="course-live-container" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
                padding: '40px',
                background: '#fff',
                borderRadius: '8px'
              }}>
                <Title level={3}>课程直播</Title>
                <Paragraph style={{ textAlign: 'center', marginBottom: '30px' }}>
                  点击下方按钮进入课程直播平台
                </Paragraph>
                <Button
                  type="primary"
                  size="large"
                  icon={<DashboardOutlined />}
                  onClick={() => window.open('https://dmtutors.live', '_blank')}
                  style={{ fontSize: '16px', height: '50px', padding: '0 40px' }}
                >
                  进入直播平台
                </Button>
              </div>
            ) : activeMenuKey === 'courses' ? (
              renderCourseSchedule()
            ) : (
              <Card 
                title={selectedStudent ? `${selectedStudent.name}的学习成果` : "我的学员"} 
                loading={loading.students}
                extra={
                  selectedStudent ? (
                    <Space>
                      <Button 
                        onClick={() => setSelectedStudent(null)}
                      >
                        返回学员列表
                      </Button>
                      <Button 
                        icon={<ReloadOutlined />}
                        onClick={() => fetchStudentAchievements(selectedStudent.id)}
                        loading={loading.achievements}
                      >
                        刷新
                      </Button>
                      <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={showAddAchievementModal}
                      >
                        添加学习成果
                      </Button>
                    </Space>
                  ) : (
                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />} 
                      onClick={showAddStudentModal}
                    >
                      添加学员
                    </Button>
                  )
                }
              >
                {selectedStudent ? (
                  <div className="achievements-container">
                    {loading.achievements ? (
                      <div style={{ padding: '40px 0', textAlign: 'center' }}>
                        <Spin tip="正在加载学习成果..." size="large" />
                      </div>
                    ) : achievements.length === 0 ? (
                      <Empty 
                        description={
                          <div>
                            <p>该学员暂无学习成果记录</p>
                            <p className="empty-note">点击下方按钮添加学习成果</p>
                          </div>
                        }
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      >
                        <Space direction="vertical" style={{ width: '100%', maxWidth: '400px' }}>
                          <Button 
                            type="primary" 
                            icon={<PlusOutlined />}
                            onClick={showAddAchievementModal}
                          >
                            添加第一个学习成果
                          </Button>
                        </Space>
                      </Empty>
                    ) : (
                      // 学习成果列表
                      <List
                        itemLayout="vertical"
                        dataSource={achievements}
                        renderItem={(item: StudentAchievement, index: number) => (
                          <List.Item
                            key={item.id}
                            className="achievement-item"
                            actions={[
                              <Button key="delete" type="link" danger onClick={() => handleDeleteAchievement(item.id)}>删除</Button>
                            ]}
                          >
                            <div className="achievement-content">
                              {/* 序号 */}
                              <div className="achievement-index">
                                {index + 1}
                              </div>
                              
                              {/* 内容区域 */}
                              <div className="achievement-details">
                                {/* 日期和标题 */}
                                <div className="achievement-header">
                                  <Tag color="blue">
                                    {moment(item.achievementDate).format('YYYY-MM-DD')}
                                  </Tag>
                                  <span className="achievement-title">
                                    {item.title || '未命名成果'}
                                  </span>
                                </div>
                                
                                {/* 描述 */}
                                <Paragraph className="achievement-description">
                                  {item.description}
                                </Paragraph>
                                
                                {/* 图片 */}
                                {(() => {
                                  // 检查图片URL是否有效
                                  if (item.imageUrl) {
                                    return (
                                      <div className="achievement-image">
                                        <div className="achievement-image-container">
                                          <Image
                                            width={220}
                                            height={160}
                                            src={item.imageUrl}
                                            alt={`${item.title || '学习成果'}证明图片`}
                                            fallback={`${process.env.PUBLIC_URL}/images/default-image.jpg`}
                                            style={{ 
                                              objectFit: 'cover', 
                                              borderRadius: '8px',
                                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                            }}
                                            preview={{
                                              src: item.imageUrl,
                                              mask: (
                                                <div>
                                                  <span>查看大图</span>
                                                </div>
                                              )
                                            }}
                                            onError={(e) => {
                                              console.error('证明图片加载失败:', item.imageUrl);
                                              const target = e.currentTarget as HTMLImageElement;
                                              target.onerror = null; // 防止循环触发
                                              target.src = `${process.env.PUBLIC_URL}/images/default-image.jpg`;
                                              return false;
                                            }}
                                          />
                                          <div className="achievement-image-badge">证明图片</div>
                                        </div>
                                        
                                        {/* 显示图片URL，仅用于调试 */}
                                        {process.env.NODE_ENV === 'development' && (
                                          <div style={{ fontSize: '10px', color: '#999', marginTop: '4px', wordBreak: 'break-all' }}>
                                            URL: {item.imageUrl.substring(0, 50)}...
                                          </div>
                                        )}
                                      </div>
                                    );
                                  } else {
                                    // 未上传图片 - 显示默认提示
                                    return (
                                      <div className="achievement-no-image">
                                        <div className="no-image-text">未上传证明图片</div>
                                      </div>
                                    );
                                  }
                                })()}
                              </div>
                            </div>
                          </List.Item>
                        )}
                      />
                    )}
                  </div>
                ) : (
                  // 重新实现学生列表，不使用点击行跳转
                  <div className="student-table-container">
                    {loading.students ? (
                      <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Spin tip="加载学员列表中..." />
                      </div>
                    ) : students.length === 0 ? (
                      <Empty description="暂无学员，请添加" />
                    ) : (
                      <div className="custom-table">
                        {/* 表头 */}
                        <div className="custom-table-header">
                          <div className="custom-table-row">
                            <div className="custom-table-cell custom-table-cell-index">序号</div>
                            <div className="custom-table-cell">学员姓名</div>
                            {!isMobile && <div className="custom-table-cell">年龄</div>}
                            {!isMobile && <div className="custom-table-cell">性别</div>}
                            {!isMobile && <div className="custom-table-cell">年级</div>}
                            {!isMobile && <div className="custom-table-cell">授课类型</div>}
                            {!isMobile && <div className="custom-table-cell">授课方式</div>}
                            <div className="custom-table-cell">
                              <Space>
                                <UserOutlined />
                                <span>任课教师</span>
                                <span style={{ fontSize: '12px', color: '#666' }}>(点击选择)</span>
                              </Space>
                            </div>
                            <div className="custom-table-cell">操作</div>
                          </div>
                        </div>
                        
                        {/* 表格内容 */}
                        <div className="custom-table-body">
                          {students.map((student, index) => (
                            <div className="custom-table-row" key={student.id} onClick={(e) => e.stopPropagation()}>
                              <div className="custom-table-cell custom-table-cell-index">{index + 1}</div>
                              <div className="custom-table-cell">{student.name}</div>
                              {!isMobile && (
                                <div className="custom-table-cell">
                                  {student.birthDate ? (
                                    <Tooltip title={`根据出生日期 ${moment(student.birthDate).format('YYYY-MM-DD')} 计算`}>
                                      {student.age || '--'}
                                    </Tooltip>
                                  ) : (
                                    student.age || '--'
                                  )}
                                </div>
                              )}
                              {!isMobile && <div className="custom-table-cell">{student.gender}</div>}
                              {!isMobile && (
                                <div className="custom-table-cell">
                                  {student.grade ? (
                                    (() => {
                                      // 高亮显示年级，不同阶段使用不同颜色
                                      let color = 'default';
                                      if (student.grade.includes('幼儿园')) {
                                        color = 'purple';
                                      } else if (student.grade.includes('小学')) {
                                        color = 'blue';
                                      } else if (student.grade.includes('初中')) {
                                        color = 'green';
                                      } else if (student.grade.includes('高中')) {
                                        color = 'orange';
                                      }
                                      
                                      return <Tag color={color}>{student.grade}</Tag>;
                                    })()
                                  ) : '未知'}
                                </div>
                              )}
                              {!isMobile && <div className="custom-table-cell">{student.courseType}</div>}
                              {!isMobile && <div className="custom-table-cell">{student.courseMode}</div>}
                              <div className="custom-table-cell">
                                <div 
                                  style={{ 
                                    cursor: 'pointer', 
                                    display: 'flex', 
                                    width: '100%', 
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '4px 8px',
                                    border: '1px dashed #d9d9d9',
                                    borderRadius: '4px',
                                    backgroundColor: '#f5f5f5'
                                  }}
                                    onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                      showSelectTeacherModal(student);
                                    }}
                                  >
                                  <div>
                                    {student.teacherName ? (
                                      <Space>
                                        <UserOutlined />
                                        <span>{student.teacherName}</span>
                                      </Space>
                                    ) : (
                                      <span style={{ color: '#999' }}>未分配</span>
                                    )}
                                  </div>
                                  <div>
                                    {student.teacherName ? (
                                      <EditOutlined style={{ color: '#1890ff' }} />
                                    ) : (
                                      <PlusOutlined style={{ color: '#1890ff' }} />
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="custom-table-cell">
                                <Space wrap={isMobile} direction={isMobile ? "vertical" : "horizontal"}>
                                  <Button 
                                    size="small" 
                                    icon={<TrophyOutlined />}
                                    onClick={() => showStudentAchievements(student, true)}
                                  >
                                    学习成果
                                  </Button>
                                  <Button 
                                    size="small" 
                                    icon={<EditOutlined />}
                                    onClick={() => showEditStudentModal(student)}
                                  >
                                    编辑
                                  </Button>
                                  <Button 
                                    size="small" 
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleDeleteStudent(student.id)}
                                  >
                                    删除
                                  </Button>
                                </Space>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* 自定义表格样式 */}
                    <style>{`
                      .student-table-container {
                        overflow-x: auto;
                        width: 100%;
                      }
                      .custom-table {
                        width: 100%;
                        border-collapse: collapse;
                        border: 1px solid #f0f0f0;
                        border-radius: 2px;
                        table-layout: fixed;
                      }
                      .custom-table-header {
                        background-color: #fafafa;
                        border-bottom: 1px solid #f0f0f0;
                        font-weight: 500;
                      }
                      .custom-table-row {
                        display: flex;
                        border-bottom: 1px solid #f0f0f0;
                        min-height: 54px;
                      }
                      .custom-table-body .custom-table-row:last-child {
                        border-bottom: none;
                      }
                      .custom-table-cell {
                        padding: 12px 8px;
                        flex: 1;
                        display: flex;
                        align-items: center;
                        overflow: visible;
                        white-space: normal;
                        word-break: break-word;
                        min-height: 100%;
                      }
                      .custom-table-cell:last-child {
                        flex: 1.5;
                      }
                      .custom-table-cell-index {
                        flex: 0.5;
                        justify-content: center;
                        font-weight: 500;
                      }
                      .custom-table-body {
                        max-height: none;
                      }
                      
                      /* 优化按钮布局 */
                      .custom-table-cell .ant-space {
                        flex-wrap: wrap;
                      }
                      .custom-table-cell .ant-btn {
                        margin: 2px;
                      }
                      
                      /* 在移动端优化显示 */
                      @media (max-width: 768px) {
                        .custom-table-cell {
                          padding: 8px 4px;
                        }
                        .custom-table-cell-index {
                          flex: 0.3;
                        }
                      }
                    `}</style>
                  </div>
                )}
              </Card>
            )}
          </Content>
        </Layout>
      </Layout>

      {/* 添加学生模态框 */}
      <Modal
        title="添加学员"
        visible={isAddStudentModalVisible}
        onOk={handleAddStudent}
        onCancel={() => setIsAddStudentModalVisible(false)}
        okText="确认"
        cancelText="取消"
        width={isMobile ? "95%" : 520}
        centered
      >
        <Form form={studentForm} layout="vertical">
          <Form.Item
            name="name"
            label="学员姓名"
            rules={[{ required: true, message: '请输入学员姓名' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="birthDate"
            label="出生日期"
            rules={[{ required: true, message: '请选择出生日期' }]}
          >
            <DatePicker style={{ width: '100%' }} placeholder="请选择出生日期" />
          </Form.Item>
          <Form.Item
            name="gender"
            label="性别"
            rules={[{ required: true, message: '请选择性别' }]}
          >
            <Select>
              <Option value="男">男</Option>
              <Option value="女">女</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="grade"
            label="在读年级"
            rules={[{ required: true, message: '请选择在读年级' }]}
          >
            <Select placeholder="请选择在读年级">
              <Option value="幼儿园小班">幼儿园小班</Option>
              <Option value="幼儿园中班">幼儿园中班</Option>
              <Option value="幼儿园大班">幼儿园大班</Option>
              <Option value="小学一年级">小学一年级</Option>
              <Option value="小学二年级">小学二年级</Option>
              <Option value="小学三年级">小学三年级</Option>
              <Option value="小学四年级">小学四年级</Option>
              <Option value="小学五年级">小学五年级</Option>
              <Option value="小学六年级">小学六年级</Option>
              <Option value="初中一年级">初中一年级</Option>
              <Option value="初中二年级">初中二年级</Option>
              <Option value="初中三年级">初中三年级</Option>
              <Option value="高中一年级">高中一年级</Option>
              <Option value="高中二年级">高中二年级</Option>
              <Option value="高中三年级">高中三年级</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="courseType"
            label="授课类型"
            rules={[{ required: true, message: '请选择授课类型' }]}
          >
            <Select placeholder="请选择授课类型">
              <Option value="一对一">一对一</Option>
              <Option value="一对二">一对二</Option>
              <Option value="一对三">一对三</Option>
              <Option value="一对一（网课）">一对一（网课）</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="courseMode"
            label="授课方式"
            rules={[{ required: true, message: '请选择授课方式' }]}
          >
            <Select placeholder="请选择授课方式">
              <Option value="线上">线上</Option>
              <Option value="线下">线下</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑学生模态框 */}
      <Modal
        title="编辑学员信息"
        visible={isEditStudentModalVisible}
        onOk={handleEditStudent}
        onCancel={() => setIsEditStudentModalVisible(false)}
        okText="确认"
        cancelText="取消"
        width={isMobile ? "95%" : 520}
        centered
      >
        <Form form={studentForm} layout="vertical">
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="name"
            label="学员姓名"
            rules={[{ required: true, message: '请输入学员姓名' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="birthDate"
            label="出生日期"
            rules={[{ required: true, message: '请选择出生日期' }]}
          >
            <DatePicker style={{ width: '100%' }} placeholder="请选择出生日期" />
          </Form.Item>
          <Form.Item
            name="gender"
            label="性别"
            rules={[{ required: true, message: '请选择性别' }]}
          >
            <Select>
              <Option value="男">男</Option>
              <Option value="女">女</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="grade"
            label="在读年级"
            rules={[{ required: true, message: '请选择在读年级' }]}
          >
            <Select placeholder="请选择在读年级">
              <Option value="幼儿园小班">幼儿园小班</Option>
              <Option value="幼儿园中班">幼儿园中班</Option>
              <Option value="幼儿园大班">幼儿园大班</Option>
              <Option value="小学一年级">小学一年级</Option>
              <Option value="小学二年级">小学二年级</Option>
              <Option value="小学三年级">小学三年级</Option>
              <Option value="小学四年级">小学四年级</Option>
              <Option value="小学五年级">小学五年级</Option>
              <Option value="小学六年级">小学六年级</Option>
              <Option value="初中一年级">初中一年级</Option>
              <Option value="初中二年级">初中二年级</Option>
              <Option value="初中三年级">初中三年级</Option>
              <Option value="高中一年级">高中一年级</Option>
              <Option value="高中二年级">高中二年级</Option>
              <Option value="高中三年级">高中三年级</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="courseType"
            label="授课类型"
            rules={[{ required: true, message: '请选择授课类型' }]}
          >
            <Select placeholder="请选择授课类型">
              <Option value="一对一">一对一</Option>
              <Option value="一对二">一对二</Option>
              <Option value="一对三">一对三</Option>
              <Option value="一对一（网课）">一对一（网课）</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="courseMode"
            label="授课方式"
            rules={[{ required: true, message: '请选择授课方式' }]}
          >
            <Select placeholder="请选择授课方式">
              <Option value="线上">线上</Option>
              <Option value="线下">线下</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 选择教师模态框 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <UserSwitchOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            <span>{selectedStudent ? `为 ${selectedStudent.name} 选择教师` : '选择教师'}</span>
          </div>
        }
        visible={isSelectTeacherModalVisible}
        onOk={(e) => {
          if (e && 'preventDefault' in e) {
            // 保留阻止默认行为，这不会影响Modal按钮功能
            e.preventDefault();
            e.stopPropagation();
          }
          handleSelectTeacher();
        }}
        onCancel={(e?: React.MouseEvent<HTMLElement>) => {
          if (e) {
            // 保留阻止默认行为，这不会影响Modal按钮功能
            e.preventDefault();
            e.stopPropagation();
          }
          setIsSelectTeacherModalVisible(false);
        }}
        okText="确认选择"
        cancelText="取消"
        width={isMobile ? "95%" : 600}
        centered
        maskClosable={false}
        destroyOnClose={false}
        getContainer={() => document.body}
        style={{ zIndex: 1050 }}
      >
        {/* 为Radio按钮添加选中样式 */}
        <style>{`
          .teacher-radio-button.ant-radio-button-wrapper-checked {
            border: 2px solid #1890ff !important;
            box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.2);
            background-color: rgba(24, 144, 255, 0.05);
          }
          
          .teacher-radio-button.ant-radio-button-wrapper-checked::after {
            content: "✓";
            position: absolute;
            top: 10px;
            right: 10px;
            width: 24px;
            height: 24px;
            background-color: #1890ff;
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          }
          
          .teacher-radio-button.ant-radio-button-wrapper:hover {
            border-color: #40a9ff !important;
            background-color: rgba(24, 144, 255, 0.03);
            transform: translateY(-2px);
            transition: all 0.3s ease;
          }

          .teacher-avatar {
            transition: all 0.3s ease;
          }
          
          .teacher-radio-button.ant-radio-button-wrapper-checked .teacher-avatar {
            transform: scale(1.05);
            border: 2px solid #1890ff;
          }
        `}</style>
        
        {/* 增加点击拦截层，用div替代直接在Modal上的onClick */}
        <div 
          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
            // 只阻止冒泡，不阻止默认行为
            e.stopPropagation();
          }}
          style={{ position: 'relative' }}
      >
        {loading.teachers ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin tip="加载教师列表中..." />
          </div>
        ) : (
          <div>
            {teachers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Empty 
                    description={
                      teacherServerError 
                        ? "服务器暂时无法提供教师列表，请联系管理员" 
                        : "暂无可选教师或加载失败"
                    } 
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                  {!teacherServerError && (
                    <Button 
                      icon={<ReloadOutlined />} 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setLoading(prev => ({ ...prev, teachers: true }));
                        loadTeachers().finally(() => {
                          setLoading(prev => ({ ...prev, teachers: false }));
                        });
                      }}
                      style={{ marginTop: '16px' }}
                    >
                      重新加载教师列表
                    </Button>
                  )}
                  {teacherServerError && (
                    <div style={{ marginTop: '16px', color: '#666' }}>
                      <p>您仍然可以通过以下方式处理：</p>
                      <ol style={{ textAlign: 'left' }}>
                        <li>稍后再试</li>
                        <li>刷新页面后重试</li>
                        <li>联系管理员手动分配教师</li>
                      </ol>
                      <div style={{ marginTop: '20px', textAlign: 'left' }}>
                        <Form form={teacherForm} layout="vertical">
                          <Form.Item
                            name="studentId"
                            hidden
                          >
                            <Input type="hidden" />
                          </Form.Item>
                          <Form.Item
                            name="teacherId"
                            label="手动输入教师ID"
                            rules={[{ required: true, message: '请输入教师ID' }]}
                            help="如果您知道教师ID，可以在此处手动输入"
                          >
                            <Input type="number" placeholder="请输入教师ID" />
                          </Form.Item>
                        </Form>
                      </div>
                    </div>
                  )}
                </div>
            ) : (
              <Form form={teacherForm} layout="vertical">
                  <Form.Item
                    name="studentId"
                    hidden
                  >
                    <Input type="hidden" />
                  </Form.Item>
                <Form.Item
                  name="teacherId"
                  rules={[{ required: true, message: '请选择教师' }]}
                >
                  <Radio.Group style={{ width: '100%' }}>
                    {teachers.map(teacher => (
                      <Radio.Button 
                        key={teacher.id} 
                        value={teacher.id}
                        style={{ 
                          display: 'block', 
                          height: 'auto', 
                            padding: '12px 16px',
                            marginBottom: '12px',
                          width: '100%',
                            textAlign: 'left',
                            borderRadius: '8px',
                            border: '1px solid #f0f0f0',
                            position: 'relative',
                          }}
                          className="teacher-radio-button"
                          onClick={(e: React.MouseEvent<HTMLElement>) => {
                            // 只阻止冒泡，不阻止默认行为
                            e.stopPropagation();
                          }}
                        >
                          <div 
                            style={{ display: 'flex', alignItems: 'flex-start' }}
                            onClick={(e: React.MouseEvent<HTMLElement>) => {
                              // 只阻止冒泡，不阻止默认行为
                              e.stopPropagation();
                            }}
                          >
                            <div
                              className="teacher-avatar"
                              style={{
                                width: 48,
                                height: 48,
                                marginRight: '16px',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                flexShrink: 0
                              }}
                              onClick={(e?: React.MouseEvent<HTMLElement>) => {
                                // 只阻止冒泡，不阻止默认行为
                                if (e) e.stopPropagation();
                              }}
                            >
                              <img 
                                src={teacher.profileImage} 
                                alt={teacher.chineseName || teacher.name} 
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src = `${process.env.PUBLIC_URL}/images/default-teacher.jpg`;
                                }}
                              />
                            </div>
                            <div 
                              style={{ flex: 1 }}
                              onClick={(e: React.MouseEvent<HTMLElement>) => {
                                // 只阻止冒泡，不阻止默认行为
                                e.stopPropagation();
                              }}
                            >
                              <div 
                                style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                  // 只阻止冒泡，不阻止默认行为
                                  e.stopPropagation();
                                }}
                              >
                                {teacher.chineseName || teacher.name}
                          </div>
                              <div 
                                style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                  // 只阻止冒泡，不阻止默认行为
                                  e.stopPropagation();
                                }}
                              >
                                {teacher.title}
                                {teacher.teachingYears && ` · ${teacher.teachingYears}年教学经验`}
                              </div>
                              <div 
                                style={{ fontSize: '12px', color: '#999' }}
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                  // 只阻止冒泡，不阻止默认行为
                                  e.stopPropagation();
                                }}
                              >
                                {teacher.description || teacher.personalStatement || '要选好老师就在Teacher私下聊！'}
                              </div>
                              {(teacher.specialties || teacher.teachingContent) && (
                                <div 
                                  style={{ marginTop: '8px' }}
                                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                                    // 只阻止冒泡，不阻止默认行为
                                    e.stopPropagation();
                                  }}
                                >
                                  {/* 处理数组格式 */}
                                  {Array.isArray(teacher.specialties) && 
                                    teacher.specialties.map((tag: string, index: number) => 
                                      tag && tag.trim() ? (
                                        <Tag 
                                          key={`specialty-${index}`} 
                                          color="blue" 
                                          style={{ marginBottom: '4px' }}
                                          onClick={(e?: React.MouseEvent<HTMLElement>) => {
                                            if (e) {
                                              // 只阻止冒泡，不阻止默认行为
                                              e.stopPropagation();
                                            }
                                          }}
                                        >
                                          {tag.trim()}
                                        </Tag>
                                      ) : null
                                    )
                                  }
                                  
                                  {Array.isArray(teacher.teachingContent) && 
                                    teacher.teachingContent.map((tag: string, index: number) => 
                                      tag && tag.trim() ? (
                                        <Tag 
                                          key={`content-${index}`} 
                                          color="green" 
                                          style={{ marginBottom: '4px' }}
                                          onClick={(e?: React.MouseEvent<HTMLElement>) => {
                                            if (e) {
                                              // 只阻止冒泡，不阻止默认行为
                                              e.stopPropagation();
                                            }
                                          }}
                                        >
                                          {tag.trim()}
                                        </Tag>
                                      ) : null
                                    )
                                  }
                                  
                                  {/* 处理字符串格式（向后兼容） */}
                                  {typeof teacher.specialties === 'string' && teacher.specialties && 
                                    teacher.specialties.split(',').map((tag: string, index: number) => 
                                      tag.trim() ? (
                                        <Tag 
                                          key={`str-specialty-${index}`} 
                                          color="blue" 
                                          style={{ marginBottom: '4px' }}
                                          onClick={(e?: React.MouseEvent<HTMLElement>) => {
                                            if (e) {
                                              // 只阻止冒泡，不阻止默认行为
                                              e.stopPropagation();
                                            }
                                          }}
                                        >
                                          {tag.trim()}
                                        </Tag>
                                      ) : null
                                    )
                                  }
                                  
                                  {typeof teacher.teachingContent === 'string' && teacher.teachingContent && 
                                    teacher.teachingContent.split(',').map((tag: string, index: number) => 
                                      tag.trim() ? (
                                        <Tag 
                                          key={`str-content-${index}`} 
                                          color="green" 
                                          style={{ marginBottom: '4px' }}
                                          onClick={(e?: React.MouseEvent<HTMLElement>) => {
                                            if (e) {
                                              // 只阻止冒泡，不阻止默认行为
                                              e.stopPropagation();
                                            }
                                          }}
                                        >
                                          {tag.trim()}
                                        </Tag>
                                      ) : null
                                    )
                                  }
                                </div>
                              )}
                            </div>
                          </div>
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </Form.Item>
              </Form>
            )}
          </div>
        )}
        </div>
      </Modal>

      {/* 添加学习成果模态框 */}
      <Modal
        title="添加学习成果"
        visible={isAchievementModalVisible}
        onOk={handleAddAchievement}
        onCancel={() => setIsAchievementModalVisible(false)}
        okText="确认"
        cancelText="取消"
        width={isMobile ? "95%" : 520}
        centered
      >
        <Form form={achievementForm} layout="vertical">
          <Form.Item
            name="achievementDate"
            label="日期"
            rules={[{ required: true, message: '请选择日期' }]}
          >
            <DatePicker />
          </Form.Item>
          <Form.Item
            name="title"
            label="成果名称"
            rules={[{ required: true, message: '请输入成果名称' }]}
          >
            <Input placeholder="例如：KET、PET成果" />
          </Form.Item>
          <Form.Item
            name="description"
            label="成果详细描述"
            rules={[{ required: true, message: '请输入详细描述' }]}
          >
            <TextArea rows={4} placeholder="请详细描述学习成果内容和收获..." />
          </Form.Item>
          <Form.Item
            name="image"
            label="上传成果证明图片"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e && e.fileList ? e.fileList.map((file: UploadFile) => {
                // 确保保留原始文件对象
                if (file.originFileObj) {
                  console.log('文件对象检查:', {
                    hasOriginFileObj: !!file.originFileObj,
                    type: typeof file.originFileObj,
                    isFile: file.originFileObj instanceof File,
                    name: file.originFileObj.name,
                    size: file.originFileObj.size
                  });
                }
                return file;
              }) : [];
            }}
            extra="支持JPG、PNG、GIF格式，最大2MB"
          >
            <Upload 
              maxCount={1} 
              listType="picture-card"
              accept="image/*"
              beforeUpload={(file) => {
                // 校验文件类型
                const isImage = file.type.startsWith('image/');
                if (!isImage) {
                  message.error('只能上传JPG、PNG、GIF格式的图片文件!');
                  return Upload.LIST_IGNORE;
                }
                
                // 校验文件大小
                const isLt2M = file.size / 1024 / 1024 < 2;
                if (!isLt2M) {
                  message.error('图片必须小于2MB!');
                  return Upload.LIST_IGNORE;
                }
                
                // 添加文件预览
                if (isImage && window.FileReader) {
                  const reader = new FileReader();
                  reader.onload = () => {
                    // 使用类型断言处理预览
                    (file as ExtendedRcFile).preview = reader.result as string;
                  };
                  reader.readAsDataURL(file);
                }
                
                // 记录文件信息
                console.log('预检通过的图片:', {
                  name: file.name,
                  size: (file.size / 1024).toFixed(2) + 'KB',
                  type: file.type
                });
                
                // 返回false阻止自动上传
                return false;
              }}
              customRequest={({ file, onSuccess }) => {
                // 模拟成功上传，确保文件对象保留在fileList中
                setTimeout(() => {
                  onSuccess?.("ok");
                }, 0);
              }}
            >
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>上传图片</div>
              </div>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* 添加图片样式 */}
      <style>{`
        .achievement-image-container {
          position: relative;
          margin-top: 12px;
          display: inline-block;
        }
        
        .achievement-image-badge {
          position: absolute;
          top: 8px;
          right: 8px;
          background-color: rgba(24, 144, 255, 0.8);
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
        }
        
        .achievement-no-image {
          margin-top: 12px;
          border: 1px dashed #d9d9d9;
          border-radius: 8px;
          padding: 10px;
          background-color: #fafafa;
          text-align: center;
          width: 220px;
        }
        
        .no-image-text {
          color: #999;
          font-size: 14px;
        }
        
        /* 日历样式 */
        .course-schedule-container {
          padding: 24px;
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        
        .calendar-container {
          margin-top: 24px;
          background: #fff;
          border-radius: 8px;
          padding: 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }
        
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }
        
        /* 优化日历事件样式 */
        .rbc-event {
          background-color: #1890ff;
          border-radius: 4px;
          padding: 2px 5px;
          font-size: 12px;
        }
        
        .rbc-today {
          background-color: #e6f7ff;
        }
      `}</style>
    </>
  );
};

export default ParentDashboard; 